import React, {
  useCallback,
  useEffect,
  useState
} from 'react';

import axios from 'axios';
import {
  ArrowDown,
  ArrowUp,
  FileAudio,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Trash2
} from 'lucide-react';

import { useSettings } from '../contexts/SettingsContext';

const API = (
  process.env.REACT_APP_API_URL ||
  'http://187.127.34.239:4001/api'
).replace(/\/$/, '');

const BASE_URL = API.replace(
  '/api',
  ''
);

const KINDS = [
  {
    id: 'texto',
    label: 'Texto'
  },
  {
    id: 'pdf',
    label: 'PDF'
  },
  {
    id: 'audio',
    label: 'Áudio'
  }
];

const EMPTY_FORM = {
  kind: 'texto',
  title: '',
  text_content: '',
  active: true
};

function formatDate(value) {
  if (!value) return '';

  try {
    return new Date(value)
      .toLocaleString('pt-BR');
  } catch (_) {
    return '';
  }
}

function kindLabel(kind) {
  return (
    KINDS.find(
      item => item.id === kind
    )?.label || kind
  );
}

export default function RatingContent() {
  const { darkMode } =
    useSettings();

  const [items, setItems] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const [message, setMessage] =
    useState('');

  const [form, setForm] =
    useState(EMPTY_FORM);

  const [file, setFile] =
    useState(null);

  const [editingId, setEditingId] =
    useState(null);

  const [formOpen, setFormOpen] =
    useState(false);

  const card =
    darkMode
      ? 'bg-gray-800 border-gray-700 text-gray-100'
      : 'bg-white border-gray-200 text-gray-900';

  const muted =
    darkMode
      ? 'text-gray-400'
      : 'text-gray-500';

  const input =
    darkMode
      ? 'bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';

  const loadItems =
    useCallback(async () => {
      setLoading(true);

      try {
        const response =
          await axios.get(
            `${API}/rating-sequence/items`
          );

        setItems(
          Array.isArray(
            response.data?.data
          )
            ? response.data.data
            : []
        );

        setError('');
      } catch (requestError) {
        setError(
          requestError.response
            ?.data?.error ||
          requestError.message ||
          'Erro ao carregar a sequência'
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  function resetForm() {
    setForm(EMPTY_FORM);
    setFile(null);
    setEditingId(null);
    setFormOpen(false);
  }

  function openNewForm() {
    setForm(EMPTY_FORM);
    setFile(null);
    setEditingId(null);
    setFormOpen(true);
    setError('');
    setMessage('');
  }

  function openEditForm(item) {
    setForm({
      kind: item.kind,
      title: item.title || '',
      text_content:
        item.text_content || '',
      active: item.active !== false
    });

    setFile(null);
    setEditingId(item.id);
    setFormOpen(true);
    setError('');
    setMessage('');

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setSaving(true);
    setError('');
    setMessage('');

    try {
      if (
        form.kind === 'texto' &&
        !form.text_content.trim()
      ) {
        throw new Error(
          'Digite o texto do item'
        );
      }

      if (
        form.kind !== 'texto' &&
        !file &&
        !editingId
      ) {
        throw new Error(
          'Envie o arquivo do item'
        );
      }

      const data = new FormData();

      data.append(
        'kind',
        form.kind
      );

      data.append(
        'title',
        form.title
      );

      data.append(
        'text_content',
        form.text_content
      );

      data.append(
        'active',
        form.active
          ? 'true'
          : 'false'
      );

      if (file) {
        data.append(
          'file',
          file
        );
      }

      if (editingId) {
        await axios.put(
          `${API}/rating-sequence/items/${editingId}`,
          data
        );

        setMessage(
          'Item atualizado com sucesso.'
        );
      } else {
        await axios.post(
          `${API}/rating-sequence/items`,
          data
        );

        setMessage(
          'Item adicionado à sequência.'
        );
      }

      resetForm();
      await loadItems();
    } catch (requestError) {
      setError(
        requestError.response
          ?.data?.error ||
        requestError.message ||
        'Erro ao salvar o item'
      );
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(item) {
    setError('');
    setMessage('');

    try {
      const data = new FormData();

      data.append(
        'active',
        item.active
          ? 'false'
          : 'true'
      );

      await axios.put(
        `${API}/rating-sequence/items/${item.id}`,
        data
      );

      await loadItems();
    } catch (requestError) {
      setError(
        requestError.response
          ?.data?.error ||
        requestError.message ||
        'Erro ao alterar o item'
      );
    }
  }

  async function moveItem(index, direction) {
    const target =
      index + direction;

    if (
      target < 0 ||
      target >= items.length
    ) {
      return;
    }

    const next = [...items];

    [
      next[index],
      next[target]
    ] = [
      next[target],
      next[index]
    ];

    setItems(
      next.map(
        (item, position) => ({
          ...item,
          position:
            position + 1
        })
      )
    );

    setError('');
    setMessage('');

    try {
      await axios.post(
        `${API}/rating-sequence/reorder`,
        {
          ids: next.map(
            item => item.id
          )
        }
      );

      await loadItems();
    } catch (requestError) {
      setError(
        requestError.response
          ?.data?.error ||
        requestError.message ||
        'Erro ao reordenar'
      );

      await loadItems();
    }
  }

  async function removeItem(item) {
    if (
      !window.confirm(
        `Excluir o item "${item.title || kindLabel(item.kind)}"?`
      )
    ) {
      return;
    }

    setError('');
    setMessage('');

    try {
      await axios.delete(
        `${API}/rating-sequence/items/${item.id}`
      );

      setMessage(
        'Item excluído.'
      );

      await loadItems();
    } catch (requestError) {
      setError(
        requestError.response
          ?.data?.error ||
        requestError.message ||
        'Erro ao excluir o item'
      );
    }
  }

  function fileUrl(item) {
    if (!item.file_url) {
      return null;
    }

    if (
      /^https?:\/\//i.test(
        item.file_url
      )
    ) {
      return item.file_url;
    }

    return (
      BASE_URL +
      item.file_url
    );
  }

  return (
    <div
      className={`min-h-screen p-4 md:p-8 ${
        darkMode
          ? 'bg-gray-950 text-gray-100'
          : 'bg-gray-50 text-gray-900'
      }`}
    >
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <FileAudio className="w-7 h-7 text-indigo-500" />
              Sequência de Conteúdo
            </h1>

            <p className={`text-sm mt-1 ${muted}`}>
              Rating • enviada automaticamente quando o Checklist de Produção chega a 100%.
            </p>
          </div>

          <button
            type="button"
            onClick={openNewForm}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
          >
            <Plus className="w-4 h-4" />
            Adicionar item
          </button>
        </div>

        <div
          className={`rounded-xl border p-4 mb-6 ${card}`}
        >
          <p className="text-sm">
            <strong>
              Como funciona:
            </strong>{' '}
            cadastre os textos, áudios e PDFs abaixo na ordem em que o cliente deve receber.
            Assim que o 11º item do Checklist de Produção for marcado, o sistema envia toda a sequência.
            Cada cliente recebe a sequência automática uma única vez.
          </p>

          <p className={`text-xs mt-2 ${muted}`}>
            Se o WhatsApp estiver desconectado ou algum envio falhar, a sequência fica marcada como erro e pode ser reenviada no cadastro do cliente.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 text-red-700 px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {message && (
          <div className="rounded-lg border border-green-300 bg-green-50 text-green-700 px-4 py-3 mb-4 text-sm">
            {message}
          </div>
        )}

        {formOpen && (
          <form
            onSubmit={handleSubmit}
            className={`rounded-xl border p-4 md:p-5 mb-6 ${card}`}
          >
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="font-bold text-lg">
                {editingId
                  ? 'Editar item'
                  : 'Novo item'}
              </h2>

              <button
                type="button"
                onClick={resetForm}
                className={`text-sm ${muted} hover:underline`}
              >
                Cancelar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {KINDS.map(kind => (
                <button
                  key={kind.id}
                  type="button"
                  onClick={() => {
                    setForm(current => ({
                      ...current,
                      kind: kind.id
                    }));

                    setFile(null);
                  }}
                  className={`rounded-lg border px-3 py-3 text-sm font-semibold flex items-center justify-center gap-2 ${
                    form.kind === kind.id
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : darkMode
                        ? 'border-gray-700 text-gray-300'
                        : 'border-gray-300 text-gray-700'
                  }`}
                >
                  {kind.id === 'audio' ? (
                    <FileAudio className="w-4 h-4" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}

                  {kind.label}
                </button>
              ))}
            </div>

            <label className={`block text-sm font-semibold mb-1 ${muted}`}>
              Título do item
            </label>

            <input
              type="text"
              value={form.title}
              onChange={event =>
                setForm(current => ({
                  ...current,
                  title:
                    event.target.value
                }))
              }
              placeholder="Ex.: Boas-vindas, orientação inicial, PDF do contrato..."
              className={`w-full rounded-lg border px-3 py-2 mb-4 outline-none focus:ring-2 focus:ring-indigo-500 ${input}`}
            />

            {form.kind === 'texto' ? (
              <>
                <label className={`block text-sm font-semibold mb-1 ${muted}`}>
                  Texto que será enviado
                </label>

                <textarea
                  value={form.text_content}
                  onChange={event =>
                    setForm(current => ({
                      ...current,
                      text_content:
                        event.target.value
                    }))
                  }
                  rows={6}
                  placeholder="Digite a mensagem..."
                  className={`w-full rounded-lg border px-3 py-2 mb-4 outline-none focus:ring-2 focus:ring-indigo-500 resize-y ${input}`}
                />
              </>
            ) : (
              <>
                <label className={`block text-sm font-semibold mb-1 ${muted}`}>
                  Arquivo {form.kind === 'pdf' ? 'PDF' : 'de áudio'}
                </label>

                <input
                  type="file"
                  accept={
                    form.kind === 'pdf'
                      ? '.pdf,application/pdf'
                      : 'audio/*,.mp3,.ogg,.opus,.m4a,.wav,.aac,.webm'
                  }
                  onChange={event =>
                    setFile(
                      event.target
                        .files?.[0] ||
                      null
                    )
                  }
                  className={`w-full rounded-lg border px-3 py-2 mb-4 ${input}`}
                />

                {editingId && (
                  <p className={`text-xs mb-4 ${muted}`}>
                    Para manter o arquivo atual, deixe este campo vazio.
                  </p>
                )}
              </>
            )}

            <label className="inline-flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={form.active}
                onChange={event =>
                  setForm(current => ({
                    ...current,
                    active:
                      event.target.checked
                  }))
                }
                className="w-4 h-4"
              />

              Item ativo nesta sequência
            </label>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold"
              >
                {saving && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}

                {editingId
                  ? 'Salvar alterações'
                  : 'Adicionar à sequência'}
              </button>
            </div>
          </form>
        )}

        <div
          className={`rounded-xl border overflow-hidden ${card}`}
        >
          <div className="px-4 py-3 border-b border-gray-200/20 flex items-center justify-between">
            <h2 className="font-bold">
              Ordem de envio
            </h2>

            <span className={`text-xs ${muted}`}>
              {items.length} item(ns)
            </span>
          </div>

          {loading ? (
            <div className="p-8 flex items-center justify-center gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando sequência...
            </div>
          ) : items.length === 0 ? (
            <div className={`p-8 text-center text-sm ${muted}`}>
              Nenhum conteúdo cadastrado ainda.
              <br />
              Clique em <strong>Adicionar item</strong> para começar.
            </div>
          ) : (
            <div className="divide-y divide-gray-200/20">
              {items.map((item, index) => {
                const link =
                  fileUrl(item);

                return (
                  <div
                    key={item.id}
                    className="p-4 flex flex-col md:flex-row md:items-start gap-4"
                  >
                    <div className="flex md:flex-col items-center gap-1 md:gap-2">
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          darkMode
                            ? 'bg-gray-700'
                            : 'bg-gray-100'
                        }`}
                      >
                        {index + 1}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          moveItem(
                            index,
                            -1
                          )
                        }
                        disabled={index === 0}
                        className={`p-1 rounded disabled:opacity-20 ${muted}`}
                        title="Subir"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          moveItem(
                            index,
                            1
                          )
                        }
                        disabled={
                          index ===
                          items.length - 1
                        }
                        className={`p-1 rounded disabled:opacity-20 ${muted}`}
                        title="Descer"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                          {kindLabel(item.kind)}
                        </span>

                        <span
                          className={`text-xs font-semibold ${
                            item.active
                              ? 'text-green-600'
                              : 'text-gray-400'
                          }`}
                        >
                          {item.active
                            ? 'ATIVO'
                            : 'INATIVO'}
                        </span>
                      </div>

                      <h3 className="font-semibold">
                        {item.title ||
                          kindLabel(item.kind)}
                      </h3>

                      {item.kind === 'texto' && (
                        <p
                          className={`text-sm mt-1 whitespace-pre-wrap ${muted}`}
                          style={{
                            maxHeight: 90,
                            overflow: 'hidden'
                          }}
                        >
                          {item.text_content}
                        </p>
                      )}

                      {item.kind !== 'texto' && (
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <span className={`text-xs ${muted}`}>
                            {item.file_name || 'Arquivo'}
                          </span>

                          {link && (
                            <a
                              href={link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs font-semibold text-indigo-600 hover:underline"
                            >
                              {item.kind === 'audio'
                                ? 'Ouvir arquivo'
                                : 'Abrir PDF'}
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 md:flex-col md:items-stretch">
                      <button
                        type="button"
                        onClick={() =>
                          toggleActive(item)
                        }
                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                          item.active
                            ? 'border-green-300 text-green-700'
                            : 'border-gray-300 text-gray-500'
                        }`}
                      >
                        {item.active
                          ? 'Desativar'
                          : 'Ativar'}
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          openEditForm(item)
                        }
                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold inline-flex items-center justify-center gap-1 ${
                          darkMode
                            ? 'border-gray-700 text-gray-200'
                            : 'border-gray-300 text-gray-700'
                        }`}
                      >
                        <Pencil className="w-3 h-3" />
                        Editar
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          removeItem(item)
                        }
                        className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-xs font-semibold inline-flex items-center justify-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Excluir
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className={`text-xs mt-4 ${muted}`}>
          Última atualização da biblioteca:{' '}
          {formatDate(
            items[0]?.updated_at
          ) || '—'}
        </p>
      </div>
    </div>
  );
}
